import * as d3 from 'd3';
import * as _ from 'lodash';

import { IColorHelper, ColorHelper, ILogger, LoggerFactory } from '../../../helpers/helpers';
import { IAxedSeries } from './IAxedSeries';
import { TimeSeriesBase } from './TimeSeriesBase';


export class RangeSeries extends TimeSeriesBase implements IAxedSeries {
    name: string;
    isNotDisplayedData: boolean;
    preventScalingAxis: boolean;

    private _color: string;
    private _line: d3.svg.Line<any>;
    private _xScale: d3.time.Scale<any, any>;
    private _yScale;
    private _group: d3.Selection<any>;
    private _symbolsGroup: d3.Selection<any>;
    private _data: { begin: Date, end: Date }[];
    private _title: string;
    private _selectionFunction: any;
    private _identifier: any;
    private _colorHelper: IColorHelper;
    private _logger: ILogger;
    private _dateFormat;
    private _highlights: { values: any[], classes: string[], id: string, keyFunction }[] = [];

    constructor(container: d3.Selection<any>, name: string, color: string, identifier, selector, data, title) {
        super();
        this.isNotDisplayedData = true;
        this.preventScalingAxis = true;
        this.name = name;
        this.data(data);
        this._colorHelper = new ColorHelper();
        this.color(color);
        this.identifier(identifier);
        this.selector(selector);
        this.init(container, color);
        this._title = title;
        this._dateFormat = d3.time.format('%Y-%m-%d %H:%M');
    }

    private init(container: d3.Selection<any>, color: string) {
        this._logger = LoggerFactory.getLogger('WideTech.Charting.rangeSeries');

        //let currentPlot = d3.select((<any>container[0][0]).parentNode.parentNode).select('.plot-area');
        //if (currentPlot[0][0] === null) {
        //    currentPlot = d3.select('.plot-area');
        //}
        //const t = d3.transform(currentPlot.attr('transform'));

        //let defaultPane = d3.select((<any>container[0][0]).parentNode.parentNode.parentNode).select('.default-pane');
        ///*let defaultPane = d3.select('.default-pane');
        //defaultPane = (<any>container[0][0]).parentNode.parentNode;*/
        //if (defaultPane[0][0] === null) {
        //    defaultPane = d3.select('.chart-area');
        //}

        this._group = this.initBase(container);
        this._symbolsGroup = container
            .append('g')
            .classed('range-boundaries', true);

        this.updatePosition();
    }

    updatePosition() {

        //let plot = d3.select(this._group[0].parentNode).select('.plot-area');
        //if (!plot || !plot[0] || !plot[0][0]) {
        //    plot = d3.select('.plot-area');
        //}

        //if (plot && plot[0] && plot[0][0]) {
        //    const t = d3.transform(plot.attr('transform'));
        //    const x = t.translate[0];
        //    const y = t.translate[1];

        //    this._group.attr('transform', `translate(${x},${y})`);
        //    this._symbolsGroup.attr('transform', `translate(${x},${y})`);
        //}
    }


    update(data: any, xScale, yScale): void {
        this.data(data);
        this._xScale = xScale;
        this._yScale = yScale;
        const self = this;

        if (data && data.length > 0) {
            this.remove();

            data.forEach(currentData => {
                const topRange = xScale.range()[1];
                const pos1 = self._xScale(currentData.begin);
                const pos2 = self._xScale(currentData.end);

                const shouldWeDraw = (pos1 >= 0 && pos1 <= topRange) || // begin is in the range
                    (pos2 >= 0 && pos2 <= topRange) || // end is in the range
                    (pos1 < 0 && pos2 > topRange); // the range is englobed between

                if (shouldWeDraw) {
                    this.draw(currentData);
                }
            });

        } else {
            this.remove();
        }
    }

    // called by pane
    updateScales(xScale, yScale) {
        return this.update(this.data(), xScale, yScale);
    }

    private draw(currentData) {
        const group = this.addGroupAndTitle(currentData);

        this.drawLine(currentData, group);
        this.drawSymbols(currentData, group);

        this.updatePosition();
    }

    private addGroupAndTitle(currentData) {
        const title = this.getTitleForDate(currentData);

        const newGroup = this._symbolsGroup.append('g');

        if (title) {
            newGroup.append('title').text(title);
        }

        return newGroup;
    }

    private drawLine(currentData, group) {
        try {
            const self = this;

            this._line = d3.svg.line()
                .x((d: any, i) => self.intoScaleRange(d))
                .y((d, i) => 0)
                .interpolate('linear');

            group
                .append('path')
                .classed('range', true)
                .classed('trend', true)
                .style({
                    'stroke': self.color(),
                    'stroke-width': '2px'
                }).attr({
                    'd': this._line([currentData.begin, currentData.end])
                });

            if (this._highlights && this._highlights.length > 0) {
                this._highlights.forEach(h => this.setHighlight(h));
            }
        } catch (e) {
            this._logger.error(e);
        }
    }

    private drawSymbols(currentData, group) {
        try {
            this.addMarker(currentData.begin, group);
            this.addMarker(currentData.end, group);
        } catch (e) {
            this._logger.error(e);
        }
    }

    private getTitleForDate(currentData) {
        return this._title
            .replace('_begin_', this._dateFormat(currentData.begin))
            .replace('_end_', this._dateFormat(currentData.end));
    }

    // add a ponctual marker at a point
    private addMarker(value: Date, group) {
        const x = this.intoScaleRange(value);
        const y = -5 * (this._ratio || 1);

        //const newPath = this._symbolsGroup.append('path');
        const newPath = group.append('path');

        newPath.classed('range-boundary', true)
            .style({ 'fill': this.color() })
            .attr({
                'transform': (d: any, i) => {
                    let transform = `translate(${x},${y})`;
                    if (this._ratio && this._ratio !== 1) {
                        transform = `${transform} scale(${this._ratio})`;
                    }
                    return transform;
                },
                'd': d3.svg.symbol().type('triangle-down'),
            });
    }

    private intoScaleRange(value) {
        const topRange = this._xScale.range()[1];
        return Math.min(Math.max(this._xScale(value), 0), topRange);
    }

    color(value?: string): string {
        if (value) {
            this._color = value;
            //TODO update chart if color changes
        }
        return this._color;
    }

    legendText(text?: string): string {
        return null;
    }

    data(value?: any[]): { begin: Date, end: Date }[] {
        if (arguments.length) {
            this._data = value;
        }
        return this._data;
    }

    changeXScale(xScale) {
        this.update(this.data(), xScale, this._yScale);
    }

    translate(translateX: number, translateY: number) {
        //
    }

    changeScale(xScale, yScale) {
        this.update(this.data(), xScale, yScale);
    }

    selector(selectionFunction?): { (d): any } {
        if (selectionFunction) {
            this._selectionFunction = selectionFunction;
        }
        return this._selectionFunction;
    }

    identifier(value?: any): any {
        if (value !== null && value !== undefined) {
            this._identifier = value;
        }
        return this._identifier;
    }

    min(): number {
        return 0;
    }

    max(): number {
        return 1;
    }

    removeSome(currentData): void {
        this._group.select('.range')
            .data(currentData)
            .remove();
        this._symbolsGroup
            .selectAll('.range-boundary')
            .data(currentData)
            .remove();
    }

    remove(): void {
        this._symbolsGroup.selectAll('g').remove();
    }

    crosshairLabel(bisect, date, valueFormatter): string {
        return null;
    }

    resize(xScale, yScale) {
        this.changeScale(this._xScale, this._yScale);
    }

    highlight(values: any[], keyFunction: (d: any) => any, classed: string, secondaryClass: string) {
        const id = classed + secondaryClass;
        let highlight = _.find(this._highlights, h => h.id === id);
        if (values && values.length > 0 && secondaryClass === this.name) {
            if (highlight) {
                highlight.values = values;
            } else {
                highlight = {
                    classes: [classed, secondaryClass],
                    id: id,
                    values: values,
                    keyFunction: keyFunction
                };
                this._highlights.push(highlight);
            }
        } else {
            highlight = _.find(this._highlights, h => h.id.lastIndexOf(classed, 0) === 0);
            if (highlight) {
                highlight.values = [];
            }
        }
        if (highlight) {
            this.setHighlight(highlight);
        }
    }

    private setHighlight(highlight: { values: any[], classes: string[], id: string, keyFunction }) {
        const current = highlight.values;
        const classed = highlight.classes[0];
        const secondaryClass = highlight.classes[1];

        if (current && current.length > 0) {
            this._symbolsGroup
                .selectAll('.range')
                .classed(classed, true)
                .classed(secondaryClass, true);

            this._symbolsGroup
                .selectAll('.range-boundary')
                .classed(classed, true)
                .classed(secondaryClass, true);
        } else {
            this._symbolsGroup
                .selectAll(`.${classed}`)
                .classed(classed, false);

            this._symbolsGroup
                .selectAll(`.${classed}`)
                .classed(classed, false);
        }
    }

    setSerieState(clicked: boolean, state?: number, reset?: boolean) {
        //
    }

    isStacked() {
        return false;
    }

    isXAxis() {
        return false;
    }

    private _ratio: number = 1;
    ratio(): number;
    ratio(value: number): this;
    ratio(value?: number) {
        if (arguments.length) {
            this._ratio = value;
            return this;
        }
        return this._ratio;
    }
}